/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Distributor } from '@wsphere/distributors/domain';
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject, switchMap } from 'rxjs';
import { GLOBAL_DISTRIBUTORS_STORAGE_KEY, SecurityService } from '../../services';

export interface PaginatedDistributorNavigation {
  parent: Distributor.PartialModel | null;
  current: Distributor.PartialModel;
}

@Component({
  selector: 'ws-global-distributors-picker',
  templateUrl: './global-distributors-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GlobalDistributorsPickerComponent,
    },
  ],
})
export class GlobalDistributorsPickerComponent implements OnInit, OnDestroy {
  constructor(
    private securitySvc: SecurityService,
    public elementRef: ElementRef
  ) {}

  @Input() value: Distributor.PartialModel[] | null = [];
  @PropertyListener('value') value$ = new BehaviorSubject<Distributor.PartialModel[] | null>(null);

  @Input() placeholder = 'Select a distributor...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() themeColor: ThemeColor = 'primary';

  @Output() openChanged = new EventEmitter<boolean>();
  @Output() valueChanges = new EventEmitter<Distributor.PartialModel[] | null | undefined>();

  protected readonly paginatedDistributorNavigation$ = new ReplaySubject<PaginatedDistributorNavigation>(1);

  protected breadcrumbs = new Map<string, PaginatedDistributorNavigation>();

  readonly searchQuery$ = new BehaviorSubject<string>('');

  overlayOpen = false;
  touched = false;

  hasGlobalAccess = false;

  loaded = false;
  loading = false;
  theEnd = false;

  loadedDistributors: Distributor.PartialModel | null = null;

  searchedDistributors$: Observable<Distributor.PartialModel[]> = this.searchQuery$.pipe(
    switchMap(async search => {
      if (!search) return [];

      const parentId = this.currentParentId();

      const distributors = this.search(this.loadedDistributors?.children ?? [], parentId, search);

      return distributors.sort((a, b) => a.name.localeCompare(b.name));
    })
  );

  private currentParentId(): string | null {
    const crumbs = Array.from(this.breadcrumbs.keys());
    const distributorId = crumbs[crumbs.length - 1] || null;

    return distributorId === 'home' ? null : distributorId;
  }

  private search(
    distributors: Distributor.PartialModel[],
    parentId: string | null,
    search: string
  ): Distributor.PartialModel[] {
    const result: Distributor.PartialModel[] = [];

    for (const distributor of distributors) {
      const isChild = distributor.parentId === parentId;
      const hasSearch = distributor.name.toLowerCase().includes(search.toLowerCase());

      if (isChild && hasSearch) result.push(distributor);

      if (distributor.children.length) {
        result.push(...this.search(distributor.children, parentId, search));
      }
    }

    return result;
  }

  async ngOnInit() {
    await this.securitySvc.loadPermissionsMatrixFromStorage();
    const permissions = await this.securitySvc.loadPermissionsMatrixFromStorage();
    this.hasGlobalAccess = permissions.some(permission => permission.permissionKey === null);

    const stored = sessionStorage.getItem(GLOBAL_DISTRIBUTORS_STORAGE_KEY);
    if (stored) {
      const storedDistributorIds = JSON.parse(stored) as string[];
      if (storedDistributorIds.length) {
        const selectedDistributors = await this.securitySvc.getDistributorsWithAccess(undefined, storedDistributorIds);

        if (selectedDistributors.items.length) {
          this.selectValueChanged(selectedDistributors.items);
        }
      }
    }

    const initialDistributors = await this.securitySvc.getDistributorsWithAccess();
    this.loadedDistributors = {
      children: initialDistributors.items,
      permissionKey: '',
      parentId: null,
      name: '',
      id: '',
    };

    this.paginatedDistributorNavigation$.next({ parent: null, current: this.loadedDistributors });
    this.breadcrumbs.set('home', { parent: null, current: this.loadedDistributors });
    const globalDistributors = await firstValueFrom(this.securitySvc.globalDistributors$);

    if (!this.hasGlobalAccess && !globalDistributors) {
      this.value = this.loadedDistributors.children.length ? [this.loadedDistributors.children[0]] : [];
      this.securitySvc.setGlobalDistributors(this.value && this.value.length > 0 ? this.value : null);
    } else {
      this.value = globalDistributors?.length ? globalDistributors : [];
      this.securitySvc.setGlobalDistributors(this.value && this.value.length > 0 ? this.value : null);
    }
  }

  searchIncludes(distributor: Distributor.PartialModel, list: Distributor.PartialModel[]) {
    return list.some(dist => dist.id === distributor.id);
  }

  onChange = (_value: Distributor.PartialModel[] | null | undefined) => {};
  onTouched = () => {};

  selectValueChanged(value: Distributor.PartialModel[] | null) {
    this.searchQuery$.next('');
    // When "All Distributors" is selected, it adds null to the array, so we check for that and change the value to null
    if (value && value.some(v => v === null)) value = null;
    this.markAsTouched();
    this.value = value ? value.filter(value => value !== null) : value;
    this.onChange(this.value);
    this.valueChanges.next(this.value);
    this.securitySvc.setGlobalDistributors(value && value.length > 0 ? value : null);
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
    // An unselected search is not kept; only the selection persists.
    this.searchQuery$.next('');
  }

  toggle() {
    if (this.overlayOpen) this.close();
    else this.open();
  }

  select(distributor: Distributor.PartialModel) {
    if (!distributor.id) return;
    if (!this.value) this.selectValueChanged([distributor]);
    else this.selectValueChanged([...this.value, distributor]);
  }

  remove(distributor: Distributor.PartialModel) {
    const children = distributor.children?.length ? this.getDistributorChild(distributor, false, []) : [];

    if (this.value) {
      const filterChildDistributors = this.value.filter(
        distributor => !children.some(children => children.id === distributor.id)
      );

      const filteredDistributors = filterChildDistributors.filter(dist => dist.id !== distributor?.id);
      this.selectValueChanged(filteredDistributors);
    }
  }

  getDistributorChild(
    distributor: Distributor.PartialModel,
    removeSelectedChildren: boolean,
    children: Distributor.PartialModel[]
  ) {
    const result = [...children];

    if (distributor.children?.length) {
      distributor.children.forEach(child => {
        if (child.id) {
          if (removeSelectedChildren) {
            if (!this.searchIncludes(child, this.value ?? [])) {
              result.push(child);
            }
          } else {
            if (this.searchIncludes(child, this.value ?? [])) {
              result.push(child);
            }
          }
        }

        this.getDistributorChild(child, removeSelectedChildren, result);
      });
    }

    return result;
  }

  selectDistributor(distributor: Distributor.PartialModel) {
    if (!this.value) {
      this.selectValueChanged([distributor]);
    } else {
      this.selectValueChanged([...this.value, distributor]);
    }
  }

  removeDistributor(distributor: Distributor.PartialModel) {
    if (this.value) {
      this.selectValueChanged(this.value.filter(dist => dist.id !== distributor.id));
    }
  }

  async navigateAndSave(distributorId: string, route: PaginatedDistributorNavigation) {
    this.paginatedDistributorNavigation$.next(route);
    this.breadcrumbs.set(distributorId, route);
  }

  navigate(distributorId: string) {
    if (this.breadcrumbs.has(distributorId)) {
      const route = this.breadcrumbs.get(distributorId);
      if (route) {
        this.paginatedDistributorNavigation$.next(route);
      }
    }
  }

  writeValue(value: Distributor.PartialModel[] | null): void {
    this.value = value;
  }

  registerOnChange(onChange: (_value: Distributor.PartialModel[] | null | undefined) => {}): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }
  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.searchQuery$.complete();
  }
}
